import { useMemo } from 'react';
import {
  ArrowUp,
  ChevronsUpDown,
  Landmark,
  Plus,
  Settings,
  Users,
} from 'lucide-react';

import { Badge, Dialog, Skeleton, useDialog } from '@components/primitives';

import { TeamObject } from '@api/types';
import { Command } from '@components/features/CommandMenu';
import { NavigationDropdown } from '@components/features/Layout/Navigation/Dropdown';
import { useTeamAppSwitch } from '@components/features/Layout/Navigation/navigate';
import { SlantedButton } from '@components/features/Layout/Navigation/SlantedButton';
import { labelForPlan } from '@components/features/Settings/TeamBilling/utils';
import { UpgradeModal } from '@components/features/Upgrade/UpgradeCheckout/UpgradeModal';
import { useAllUserTeams, useTeamQuery } from '@queries/team';
import { TeamCreate } from '@routes/TeamSettings/Settings/TeamCreate/TeamCreate';
import { avatar } from '@utils/generateAvatar';

import css from './styles.module.css';

export interface TeamLockupProps {
  teamUuid: string;
  hideMobile?: boolean;
}

export function TeamLockup({ teamUuid, hideMobile }: TeamLockupProps) {
  const { data: team } = useTeamQuery(teamUuid);
  return (
    <span className={css.lockup}>
      <span className={css.avatar}>
        <img src={avatar(team?.name ?? '')} alt="" />
      </span>
      <span className={css.name} data-hide-mobile={hideMobile}>
        {team?.name ?? <Skeleton width={96} height={12} />}
      </span>
    </span>
  );
}

interface TierBadgeProps {
  team: TeamObject | undefined;
}
function TierBadge({ team }: TierBadgeProps) {
  if (!team) return null;

  const label = labelForPlan(team.tier);

  if (team.tier === 'pro') {
    return (
      <Badge size="sm" variant="primary" className={css.tierBadge}>
        {label}
      </Badge>
    );
  } else if (team.tier === 'enterprise') {
    return (
      <Badge size="sm" variant="warning" className={css.tierBadge}>
        {label}
      </Badge>
    );
  } else if (team.tier === 'legacy') {
    return (
      <Badge size="sm" variant="success" className={css.tierBadge}>
        {label}
      </Badge>
    );
  } else {
    return (
      <Badge size="sm" variant="outline" className={css.tierBadge}>
        {label}
      </Badge>
    );
  }
}

export interface TeamDropdownProps {
  teamUuid: string;
  appUuid?: string;
}

export function TeamDropdown({ teamUuid, appUuid }: TeamDropdownProps) {
  const { getTeamPath } = useTeamAppSwitch(teamUuid);
  const { data: team } = useTeamQuery(teamUuid);
  const { data: teams } = useAllUserTeams();

  const otherTeams = useMemo(() => {
    return teams?.filter((team) => team.uuid !== teamUuid) ?? [];
  }, [teams, teamUuid]);

  const createTeamDialog = useDialog();
  const upgradeDialog = useDialog();

  return (
    <>
      <NavigationDropdown
        className={css.dropdown}
        trigger={
          <SlantedButton suffix={<ChevronsUpDown />}>
            <TeamLockup teamUuid={teamUuid} hideMobile={!!appUuid} />
          </SlantedButton>
        }
      >
        <NavigationDropdown.Section>
          <NavigationDropdown.Item
            icon={
              <span className={css.teamAvatar}>
                <img src={avatar(team?.name ?? '')} alt="" />
              </span>
            }
            label={
              team ? (
                <span className={css.teamName}>{team.name}</span>
              ) : (
                <Skeleton width={96} height={12} />
              )
            }
            suffix={<TierBadge team={team} />}
          />

          {team?.tier === 'free' && (
            <NavigationDropdown.Item
              icon={<ArrowUp />}
              label="Upgrade"
              onClick={() => upgradeDialog.open()}
              variant="branded"
            />
          )}

          <NavigationDropdown.Item
            icon={<Settings />}
            label="Settings"
            to={`/${teamUuid}/settings/team`}
          />
          <NavigationDropdown.Item
            icon={<Users />}
            label="Members"
            to={`/${teamUuid}/settings/team/members`}
          />
          <NavigationDropdown.Item
            icon={<Landmark />}
            label="Billing"
            to={`/${teamUuid}/settings/team/billing`}
          />
        </NavigationDropdown.Section>

        {otherTeams.length > 0 && (
          <NavigationDropdown.Section label="Switch team">
            {otherTeams.map((team) => (
              <NavigationDropdown.Item
                key={team.uuid}
                icon={
                  <span className={css.teamAvatar}>
                    <img src={avatar(team?.name ?? '')} alt="" />
                  </span>
                }
                suffix={<TierBadge team={team} />}
                label={team.name}
                to={getTeamPath(team)}
                state={{ back: `/${team.uuid}` }}
              />
            ))}
          </NavigationDropdown.Section>
        )}

        <NavigationDropdown.Section dimmed>
          <NavigationDropdown.Item
            icon={<Plus />}
            label="Create new team"
            onClick={createTeamDialog.open}
          />
        </NavigationDropdown.Section>
      </NavigationDropdown>

      <Dialog state={createTeamDialog}>
        <Dialog.Header title="Create Team" />
        <Dialog.Body>
          <TeamCreate />
        </Dialog.Body>
      </Dialog>

      <UpgradeModal state={upgradeDialog} teamUuid={teamUuid} />

      {otherTeams.map((team) => (
        <Command
          key={team.uuid}
          type="route"
          id={`navigate-to-${team.uuid}`}
          label={team.name}
          group="Switch team"
          icon={<Users />}
          href={getTeamPath(team)}
          context="user"
          searchable="only"
        />
      ))}

      <Command
        type="action"
        id="create-team"
        label="Create team"
        icon={<Plus />}
        context="user"
        onSelect={createTeamDialog.open}
        group="Switch team"
        keywords={['new team']}
        searchable="only"
      />
    </>
  );
}
